import { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import StatusBadge from './StatusBadge';
import DurationDisplay from './DurationDisplay';

export default function PublicPlayer() {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  const STREAM_URL = import.meta.env.VITE_STREAM_URL || '/player/stream';

  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const { data } = await api.get('/api/dashboard');
        setNowPlaying(data.now_playing);
      } catch (err) {
        console.error('[Player] Failed to fetch now playing:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-t from-dark-900 to-dark-850 border-t border-dark-700 px-4 py-3">
      <audio ref={audioRef} src={STREAM_URL} preload="none" />

      <div className="mx-auto flex max-w-6xl items-center justify-between">
        {/* Now Playing Info */}
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={nowPlaying?.artwork_url || '/placeholder-album.png'}
            alt="album"
            className="h-12 w-12 rounded-md bg-dark-700 object-cover"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="%2364748b" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16V12M12 8h.01"/></svg>';
            }}
          />
          <div className="min-w-0">
            <p className="text-sm font-medium text-dark-100 truncate">
              {loading ? 'Loading...' : (nowPlaying?.title || 'No Track')}
            </p>
            <p className="text-xs text-dark-400 truncate">
              {nowPlaying?.artist || 'Unknown Artist'}
            </p>
          </div>
        </div>

        {/* Play Controls */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setVolume(Math.max(0, volume - 0.1))}
            className="text-dark-400 hover:text-dark-200"
          >
            🔉
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-24"
          />
          <button
            onClick={togglePlay}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700"
          >
            {isPlaying ? '❚❚' : '▶'}
          </button>
        </div>

        {/* Status */}
        <div className="flex items-center gap-3">
          <StatusBadge status={nowPlaying?.broadcast_type || 'live'} size="sm" />
          {nowPlaying?.program_name && (
            <span className="text-xs text-dark-400">
              {nowPlaying.program_name}
            </span>
          )}
          <span className="text-xs text-dark-500">
            {nowPlaying?.listener_count || 0} listeners
          </span>
        </div>
      </div>
    </div>
  );
}
